import { Chart, registerables } from 'chart.js';
import React, { useEffect, useRef, useState } from 'react';
import { getGraphCategoriesReq } from "../../service/orderService";

Chart.register(...registerables);

const OrderGraph = () => {
  const chartRef = useRef(null);
  const chartInstanceRef = useRef(null);
  const [selectedMonth, setSelectedMonth] = useState('');
  const [loading, setLoading] = useState(false);

  const formatIndianCurrency = (value) => {
    if (value >= 10000000) {
      return `₹${(value / 10000000).toFixed(1)}Cr`;
    } else if (value >= 100000) {
      return `₹${(value / 100000).toFixed(1)}L`;
    } else if (value >= 1000) {
      return `₹${(value / 1000).toFixed(1)}K`;
    }
    return `₹${value}`;
  };

  const calculateYAxisLimits = (values) => {
    if (!values || values.length === 0) return { min: 0, max: 1000 };

    const maxValue = Math.max(...values);
    const minValue = Math.min(...values);

    if (maxValue === 0) return { min: 0, max: 1000 };

    let suggestedMax;

    suggestedMax = Math.ceil(suggestedMax * 1.1);

    return {
      min: Math.min(0, minValue), 
      max: suggestedMax
    };
  };

  const getLast12Months = () => {
    const months = [];
    const now = new Date();

    for (let i = 0; i < 12; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const label = date.toLocaleString('en-IN', {
        month: 'long',
        year: 'numeric',
      });
      months.push({ value, label });
    }
    return months;
  };

  const months = getLast12Months();

  useEffect(() => {
    const now = new Date();
    const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    setSelectedMonth(currentMonth);
  }, []);

  useEffect(() => {
    if (!selectedMonth) return;

    const fetchData = async () => {
      try {
        setLoading(true);
        const payload = { startMonth: selectedMonth, endMonth: selectedMonth };
        const res = await getGraphCategoriesReq(payload);

        if (!res?.data || !res.data[0]) {
          console.log('No data available');
          return;
        }

        const monthData = res.data[0];
        const categories = monthData.categories || [];

        if (categories.length === 0) return;

        const labels = categories.map(c => c.categoryName);
        const data = categories.map(c => c.grandTotal);

        const yAxisLimits = calculateYAxisLimits(data);

        if (chartInstanceRef.current) {
          chartInstanceRef.current.destroy();
        }

        const ctx = chartRef.current.getContext('2d');
        chartInstanceRef.current = new Chart(ctx, {
          type: 'line',
          data: {
            labels: labels,
            datasets: [{
              label: `Monthly Spend (${monthData.month || selectedMonth})`,
              data: data,
              borderColor: 'rgb(37, 99, 235)',
              backgroundColor: 'rgba(37, 99, 235, 0.1)',
              borderWidth: 3,
              tension: 0.3,
              pointRadius: 5,
              fill: true,
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              tooltip: {
                callbacks: {
                  label: (context) => {
                    const value = context.parsed.y;
                    return `Amount: ₹${value.toLocaleString('en-IN')}`;
                  }
                }
              }
            },
            scales: {
              x: {
                title: {
                  display: true,
                  text: 'Categories'
                },
                ticks: {
                  maxRotation: 45,
                  minRotation: 30
                }
              },
              y: {
                beginAtZero: true,
                min: yAxisLimits.min,
                max: yAxisLimits.max,
                title: {
                  display: true,
                  text: 'Amount'
                },
                ticks: {
                  callback: function (value) {
                    return formatIndianCurrency(value);
                  }
                }
              }
            }
          }
        });

      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    return () => {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
      }
    };
  }, [selectedMonth]);

  const handleMonthChange = (e) => {
    setSelectedMonth(e.target.value);
  };

  return (
    <div style={{ width: '900px', margin: '30px auto', backgroundColor: '#fff', padding: '20px', borderRadius: '8px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <h2 style={{ fontWeight: 600 }}>Monthly Spend Overview</h2>

        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={{ fontSize: '14px', color: '#666' }}>Select Month:</span>
          <select
            value={selectedMonth}
            onChange={handleMonthChange}
            style={{ padding: '6px 10px', borderRadius: '6px', border: '1px solid #ccc', minWidth: '150px' }}
          >
            <option value="" disabled>Select Month</option>
            {months.map(m => (
              <option key={m.value} value={m.value}>{m.label}</option>
            ))}
          </select>
        </div>
      </div>

      <div style={{ width: '100%', height: '400px', position: 'relative' }}>
        {loading && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            zIndex: 10
          }}>
            Loading chart data...
          </div>
        )}
        <canvas ref={chartRef} />
      </div>

      {/* Legend for currency abbreviations */}
      <div style={{ marginTop: '15px', fontSize: '12px', color: '#666', textAlign: 'center' }}>
        <span>Note: K = Thousand (1,000), L = Lakh (1,00,000), Cr = Crore (1,00,00,000)</span>
      </div>
    </div>
  );
};

export default OrderGraph;